import React, { useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { Card, CardContent, CardHeader, Button } from '../components/ui'
import { Select } from '../components/ui/Select'
import { PageLayout } from '../layouts/PageLayout'
import { useAuth } from '../components/AuthProvider'
import { API_ENDPOINTS } from '../config/api'
import { Download, FileText, ArrowLeft, Plus, Trash2, Filter } from 'lucide-react'
import { safeJson } from '../lib/utils'

type Supplier = {
  id: string
  name: string
  contactName?: string
  email?: string
  website?: string
  categoryAccess: 'With Access' | 'No Access'
  supplierType: 'Software Supplier' | 'Hardware Supplier' | 'Services Supplier (including cloud)' | 'Administrative/Economic Materials and Services' | 'Services Supplier'
  subGradation: string
  standards: string[]
  compliance: number
  lastAssessment: string
}

const Suppliers: React.FC = () => {
  const { token } = useAuth()
  const [suppliers, setSuppliers] = useState<Supplier[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [search, setSearch] = useState('')
  const [accessFilter, setAccessFilter] = useState<'All' | 'With Access' | 'No Access'>('All')
  const [typeFilter, setTypeFilter] = useState<'All' | Supplier['supplierType']>('All')
  const [stdFilter, setStdFilter] = useState<'All' | 'NIS2' | 'SOCv2' | 'GDPR' | 'DORA'>('All')
  const [levelFilter, setLevelFilter] = useState<'All' | 'High' | 'Medium' | 'Low'>('All')
  const [sortKey, setSortKey] = useState<'name' | 'compliance' | 'lastAssessment'>('lastAssessment')
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc')
  const [addCategoryAccess, setAddCategoryAccess] = useState<'With Access' | 'No Access'>('With Access')
  const [addSupplierType, setAddSupplierType] = useState<Supplier['supplierType']>('Software Supplier')

  const supplierTypeOptions: { value: Supplier['supplierType']; label: string }[] = [
    { value: 'Software Supplier', label: 'Software Supplier' },
    { value: 'Hardware Supplier', label: 'Hardware Supplier' },
    { value: 'Services Supplier (including cloud)', label: 'Services Supplier (including cloud)' },
    { value: 'Administrative/Economic Materials and Services', label: 'Administrative/Economic Materials and Services' },
    { value: 'Services Supplier', label: 'Services Supplier' },
  ]

  const fetchSuppliers = async () => {
    setLoading(true)
    try {
      const res = await fetch(API_ENDPOINTS.SUPPLIERS, { headers: { Authorization: `Bearer ${token}` } })
      const dto = await safeJson(res)
      setSuppliers(Array.isArray(dto.suppliers) ? dto.suppliers : [])
    } catch (e: any) {
      setError(e.message || 'Failed to load suppliers')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => { fetchSuppliers() }, [token])

  const filtered = useMemo(() => {
    let rows = suppliers.filter(s => s.name.toLowerCase().includes(search.toLowerCase()))
    if (accessFilter !== 'All') rows = rows.filter(s => s.categoryAccess === accessFilter)
    if (typeFilter !== 'All') rows = rows.filter(s => s.supplierType === typeFilter)
    if (stdFilter !== 'All') rows = rows.filter(s => s.standards.includes(stdFilter))
    if (levelFilter !== 'All') {
      rows = rows.filter(s => {
        if (levelFilter === 'High') return s.compliance > 85
        if (levelFilter === 'Medium') return s.compliance >= 40 && s.compliance <= 85
        return s.compliance < 40
      })
    }
    const sorted = [...rows].sort((a, b) => {
      let vA: any, vB: any
      if (sortKey === 'name') { vA = a.name.toLowerCase(); vB = b.name.toLowerCase() }
      if (sortKey === 'compliance') { vA = a.compliance; vB = b.compliance }
      if (sortKey === 'lastAssessment') { vA = new Date(a.lastAssessment).getTime(); vB = new Date(b.lastAssessment).getTime() }
      const base = vA < vB ? -1 : vA > vB ? 1 : 0
      return sortDir === 'asc' ? base : -base
    })
    return sorted
  }, [suppliers, search, accessFilter, typeFilter, stdFilter, levelFilter, sortKey, sortDir])

  const complianceClass = (val: number) => {
    if (val > 85) return 'text-green-700 bg-green-50'
    if (val >= 40) return 'text-yellow-700 bg-yellow-50'
    return 'text-red-700 bg-red-50'
  }

  const addSupplier = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    const form = e.currentTarget as HTMLFormElement & { [key: string]: any }
    const payload = {
      name: form.name.value,
      contactName: form.contactName.value,
      email: form.email.value,
      website: form.website.value,
      categoryAccess: addCategoryAccess,
      supplierType: addSupplierType,
      subGradation: form.subGradation.value,
      standards: Array.from(form.querySelectorAll('input[name="standards"]:checked')).map((i: any)=> i.value),
      compliance: Number(form.compliance.value || 0),
      lastAssessment: form.lastAssessment.value
    }
    const res = await fetch(API_ENDPOINTS.SUPPLIERS, { method: 'POST', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` }, body: JSON.stringify(payload) })
    if (res.ok) { await fetchSuppliers(); form.reset() } else {
      const msg = await safeJson(res)
      alert(msg?.message || 'Failed to add supplier')
    }
  }

  const removeSupplier = async (id: string) => {
    if (!confirm('Delete this supplier?')) return
    const res = await fetch(`${API_ENDPOINTS.SUPPLIERS}/${id}`, { method: 'DELETE', headers: { Authorization: `Bearer ${token}` } })
    if (res.ok) fetchSuppliers()
  }

  const exportJson = (format: 'excel' | 'pdf') => {
    const payload = { filters: { search, accessFilter, typeFilter, stdFilter, levelFilter, sortKey, sortDir }, data: filtered }
    const blob = new Blob([JSON.stringify(payload, null, 2)], { type: 'application/json' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `suppliers-${new Date().toISOString().slice(0,10)}.${format}.json`
    a.click()
    URL.revokeObjectURL(url)
  }

  return (
    <PageLayout pageScope="suppliers-page" title="Suppliers"
      actions={
        <>
          <Button color="secondary" onClick={()=> exportJson('excel')}><Download /> Export Excel</Button>
          <Button color="secondary" onClick={()=> exportJson('pdf')}><FileText /> Export PDF</Button>
          <Link to="/home" className="btn btn-primary inline-flex items-center gap-2"><ArrowLeft className="h-4 w-4" /> Back to Dashboard</Link>
        </>
      }
    >
      {/* Filters */}
      <Card className="metric-card">
        <CardHeader>
          <div className="flex items-center gap-2 text-gray-700"><Filter className="h-4 w-4" /> Supplier Categories & Filters</div>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
            <div>
              <div className="form-label">Search</div>
              <input className="form-input" placeholder="Search by name" value={search} onChange={(e)=> setSearch(e.target.value)} />
            </div>
            <div>
              <div className="form-label">Access Level</div>
              <div className="flex flex-wrap gap-2">
                {(['With Access','No Access','All'] as const).map(v => (
                  <Button
                    key={v}
                    type="button"
                    size="small"
                    color={accessFilter === v ? 'primary' : 'outline'}
                    className={`px-3 py-1 ${accessFilter === v ? '' : 'bg-white'}`}
                    onClick={() => setAccessFilter(v)}
                  >
                    {v === 'All' ? 'All Suppliers' : v}
                  </Button>
                ))}
              </div>
            </div>
            <div>
              <div className="form-label">Supplier Type</div>
              <Select<string>
                className="form-input w-full"
                options={[
                  { value: 'All', label: 'All' },
                  ...supplierTypeOptions,
                ]}
                value={typeFilter}
                onChange={(v) => setTypeFilter((v ?? 'All') as typeof typeFilter)}
              />
            </div>
            <div>
              <div className="form-label">Standards</div>
              <Select<string>
                className="form-input w-full"
                options={[
                  { value: 'All', label: 'All Standards' },
                  { value: 'NIS2', label: 'NIS2' },
                  { value: 'SOCv2', label: 'SOCv2' },
                  { value: 'GDPR', label: 'GDPR' },
                  { value: 'DORA', label: 'DORA' },
                ]}
                value={stdFilter}
                onChange={(v) => setStdFilter((v ?? 'All') as typeof stdFilter)}
              />
            </div>
            <div>
              <div className="form-label">Compliance Level</div>
              <Select<string>
                className="form-input w-full"
                options={[
                  { value: 'All', label: 'All Levels' },
                  { value: 'High', label: 'High (>85%)' },
                  { value: 'Medium', label: 'Medium (40-85%)' },
                  { value: 'Low', label: 'Low (<40%)' },
                ]}
                value={levelFilter}
                onChange={(v) => setLevelFilter((v ?? 'All') as typeof levelFilter)}
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Table */}
      <Card className="metric-card">
        <CardHeader>
          <div className="flex items-center justify-between">
            <div className="text-lg font-semibold">Supplier Assessments</div>
            <details>
              <summary className="cursor-pointer btn btn-secondary flex items-center gap-2"><Plus className="h-4 w-4" /> Add Supplier</summary>
              <form className="mt-3 grid grid-cols-1 md:grid-cols-3 gap-3" onSubmit={addSupplier}>
                <input name="name" className="form-input" placeholder="Supplier Name" required />
                <input name="contactName" className="form-input" placeholder="Responsible Full Name" />
                <input name="email" className="form-input" placeholder="Email" type="email" />
                <input name="website" className="form-input" placeholder="Website" />
                <Select<string>
                  className="form-input w-full"
                  options={[
                    { value: 'With Access', label: 'With Access' },
                    { value: 'No Access', label: 'No Access' },
                  ]}
                  value={addCategoryAccess}
                  onChange={(v) => setAddCategoryAccess((v ?? 'With Access') as 'With Access' | 'No Access')}
                />
                <Select<string>
                  className="form-input w-full"
                  options={supplierTypeOptions}
                  value={addSupplierType}
                  onChange={(v) => setAddSupplierType((v ?? 'Software Supplier') as Supplier['supplierType'])}
                />
                <input name="subGradation" className="form-input" placeholder="Sub-Gradation" />
                <div className="col-span-1 md:col-span-2">
                  <div className="text-sm mb-1">Assigned Standards</div>
                  <div className="flex flex-wrap gap-3">
                    {['NIS2','SOCv2','GDPR','DORA'].map(s=> (
                      <label key={s} className="flex items-center gap-2 text-sm"><input type="checkbox" name="standards" value={s} /> {s}</label>
                    ))}
                  </div>
                </div>
                <input name="compliance" className="form-input" placeholder="Compliance %" type="number" min={0} max={100} />
                <input name="lastAssessment" className="form-input" type="date" lang={navigator.language || 'en'} />
                <div className="col-span-full flex justify-end">
                  <Button color="primary" type="submit">Save</Button>
                </div>
              </form>
            </details>
          </div>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div>Loading suppliers...</div>
          ) : error ? (
            <div className="text-red-600">{error}</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead className="bg-gray-50 text-gray-600">
                  <tr>
                    {['Supplier Name','Category','Sub-Gradation','Assigned Standards','Compliance %','Last Assessment','Actions'].map((h, idx)=> (
                      <th key={h} className="px-4 py-2 text-left font-medium cursor-pointer" onClick={()=> {
                        const map: any = {0:'name',1:'categoryAccess',2:'subGradation',3:'name',4:'compliance',5:'lastAssessment'}
                        const k = map[idx] || 'name'
                        setSortKey(k)
                        setSortDir(d => d==='asc'?'desc':'asc')
                      }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {filtered.map(s => (
                    <tr key={s.id} className="border-t border-gray-100">
                      <td className="px-4 py-2">{s.name}</td>
                      <td className="px-4 py-2">{s.categoryAccess} • {s.supplierType}</td>
                      <td className="px-4 py-2">{s.subGradation}</td>
                      <td className="px-4 py-2">
                        <div className="flex flex-wrap gap-1">{s.standards.map(st => <span key={st} className="px-2 py-0.5 rounded bg-blue-50 text-blue-700">{st}</span>)}</div>
                      </td>
                      <td className="px-4 py-2">
                        <span className={`px-2 py-1 rounded ${complianceClass(s.compliance)}`}>{s.compliance}%</span>
                      </td>
                      <td className="px-4 py-2">{new Date(s.lastAssessment).toLocaleDateString()}</td>
                      <td className="px-4 py-2">
                        <Button color="secondary" size="small" type="button" className="btn btn-secondary mr-2 px-2 py-1">View Assessment</Button>
                        <Button color="danger" size="small" type="button" className="btn btn-danger inline-flex items-center gap-1 px-2 py-1" onClick={() => removeSupplier(s.id)} icon={<Trash2 className="h-4 w-4" />}>
                          Remove
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </PageLayout>
  )
}

export default Suppliers